/**
 * Hero Video Banner
 * Handles: video readiness detection, entrance animation trigger,
 * scroll indicator click behavior, and Theme Editor live updates.
 * No external dependencies.
 */

(function () {
  'use strict';

  var SELECTOR = '.hero-video';
  var ANIMATE_DELAY = 60; // ms, lets browser paint before triggering transition classes

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Drives the "video plays, then auto-advance through slide images,
  // then loop back to video" sequence. Only ever created when at least
  // one slide image block exists; otherwise the video just replays itself
  // (see plainVideoLoop) and none of this runs.
  function createSlideshow(hero, video, slides, slideSeconds) {
    var items = [{ type: 'video', el: video }].concat(
      Array.prototype.map.call(slides, function (el) {
        return { type: 'image', el: el };
      })
    );
    var current = 0;
    var timer = null;

    function clearTimer() {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function activate(index) {
      items.forEach(function (item, i) {
        item.el.classList.toggle('is-active', i === index);
      });
    }

    function advance() {
      clearTimer();
      current = (current + 1) % items.length;
      var item = items[current];
      activate(current);

      if (item.type === 'video') {
        try {
          item.el.currentTime = 0;
        } catch (e) {
          /* ignore — some browsers throw if metadata isn't ready */
        }
        var playPromise = item.el.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // If the video can't play for some reason, don't get stuck —
          // move on to the next slide instead.
          playPromise.catch(advance);
        }
      } else {
        timer = window.setTimeout(advance, slideSeconds * 1000);
      }
    }

    // Video is already playing (autoplay) and active from initHero.
    // When it finishes, move on to the first slide. Looping back around
    // from the last slide to the video is handled by advance() itself.
    video.addEventListener('ended', function () {
      if (items[current].type === 'video') advance();
    });

    activate(0);
  }

  // No slide blocks configured: keep the original single-video behavior,
  // replaying it manually so the "ended" wiring stays consistent whether
  // or not slides are present.
  function plainVideoLoop(video) {
    video.addEventListener('ended', function () {
      try {
        video.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
      video.play();
    });
  }

  function initHero(hero) {
    if (!hero || hero.dataset.heroInitialized === 'true') return;
    hero.dataset.heroInitialized = 'true';

    var video = hero.querySelector('[data-hero-video]');
    var slides = hero.querySelectorAll('[data-hero-slide]');
    var scrollBtn = hero.querySelector('[data-hero-scroll-indicator]');
    var hasSlides = hero.dataset.heroHasSlides === 'true' && slides.length > 0;
    var slideSeconds = parseFloat(hero.dataset.heroSlideDuration) || 4;
    var reducedMotion = prefersReducedMotion();

    function markLoaded() {
      hero.classList.add('is-loaded');
      // Slight delay so opacity/transform transitions are visually distinct
      // from the "loaded" state before the text animates in.
      window.setTimeout(function () {
        hero.classList.add('is-animated');
      }, ANIMATE_DELAY);
    }

    if (video) {
      // Video is always the first thing shown, whether or not slides exist.
      video.classList.add('is-active');

      // If video metadata is already available (cached), fire immediately.
      if (video.readyState >= 2) {
        markLoaded();
      } else {
        video.addEventListener('loadeddata', markLoaded, { once: true });
        // Fallback in case 'loadeddata' never fires (e.g. autoplay blocked)
        video.addEventListener(
          'canplay',
          function () {
            if (!hero.classList.contains('is-loaded')) markLoaded();
          },
          { once: true }
        );
      }

      // Attempt to (re)play — some browsers pause programmatically
      // inserted/edited videos inside the Theme Editor.
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          // Autoplay was blocked (rare with muted+playsinline, but handle gracefully).
          // Still reveal the section so it isn't stuck hidden.
          markLoaded();
        });
      }

      if (reducedMotion) {
        video.pause();
        video.removeAttribute('autoplay');
        // Respect reduced motion: no auto-advancing slideshow, no
        // manual video replay loop. The video's first frame (or poster)
        // just sits there as a static image.
      } else if (hasSlides) {
        createSlideshow(hero, video, slides, slideSeconds);
      } else {
        plainVideoLoop(video);
      }
    } else {
      // Image or placeholder background — reveal immediately.
      markLoaded();
    }

    if (scrollBtn) {
      scrollBtn.addEventListener('click', function () {
        var next = hero.nextElementSibling;
        if (next && typeof next.scrollIntoView === 'function') {
          next.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        } else {
          window.scrollTo({
            top: hero.getBoundingClientRect().height,
            left: 0,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        }
      });
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var heroes = root.querySelectorAll(SELECTOR);
    heroes.forEach(function (hero) {
      initHero(hero);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  // Support sections rendered after initial DOMContentLoaded
  // (e.g. lazy-loaded or AJAX-injected sections).
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initAll(document);
  }

  // Shopify Theme Editor integration:
  // Re-initialize the specific section instance whenever it is
  // loaded or reloaded inside the customizer, and reset animation
  // state so merchants see the entrance animation replay on edit.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    var hero = section.querySelector(SELECTOR);
    if (!hero) return;

    hero.dataset.heroInitialized = 'false';
    hero.classList.remove('is-loaded', 'is-animated');
    hero.querySelectorAll('[data-hero-video], [data-hero-slide]').forEach(function (el) {
      el.classList.remove('is-active');
    });
    initHero(hero);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var section = event.target;
    if (!section) return;
    var hero = section.querySelector(SELECTOR);
    if (!hero) return;

    // If somehow not yet animated (e.g. video slow to trigger events
    // inside iframe preview), force reveal so merchant isn't looking
    // at a blank hero while editing.
    if (!hero.classList.contains('is-loaded')) {
      hero.classList.add('is-loaded');
      window.setTimeout(function () {
        hero.classList.add('is-animated');
      }, ANIMATE_DELAY);
    }
  });

  document.addEventListener('shopify:section:reorder', function () {
    initAll(document);
  });
})();
